/**
 * Converts a Date or DateString to String in the format: `YYYY-mm-dd`
 */
export const getDateString = (date: string | Date): string => {
  const d = date instanceof Date ? new Date(date) : date ? new Date(`${date.substring(0, 10)}T00:00:00`) : null;

  if (!isValidDate(d)) {
    return null;
  }

  const year = d.getFullYear();
  const month = `0${d.getMonth() + 1}`.slice(-2); // months from 1-12
  const day = `0${d.getDate()}`.slice(-2);
  const result = `${year}-${month}-${day}`;

  if (result.includes('NaN')) {
    console.warn(`getDateString:`, { date, result });
  }

  return result;
};

/**
 * Returns date in string format 'MM-DD-YY'
 */
const formatDate = (dateString): string => {
  const d = new Date(dateString);
  return `${(d.getMonth() + 1).toLocaleString('en-US', {
    minimumIntegerDigits: 2
  })}-${d.getDate().toLocaleString('en-US', { minimumIntegerDigits: 2 })}-${d
    .getFullYear()
    .toString()
    .slice(-2)}`;
};

export const getMonthDateString = (dateStr: string) => {
  dateStr = getDateString(dateStr);
  const monthYear = `${dateStr.slice(5, 7)}/${dateStr.slice(0, 4)}`;
  return monthYear;
};

// /**
//  * Converts date string to Date for the request's time-zone. E.g. Requests from San Francisco and from London:
//  *   getUTCDate ('2018-12-31') ==> Mon Dec 31 2018 00:00:00 GMT-0800 (Pacific Standard Time)
//  *   getUTCDate ('2018-12-31') ==> Mon Dec 31 2018 00:00:00 GMT+0000 (Greenwich Mean Time)
//  */
export const getUTCDate = (date: string | Date): Date => new Date(getDateString(date));

/**
 * Returns date in string format, e.g. `Dec 2019`
 */
export const getMonthYear = (date: Date | string): string => {
  const d = date instanceof Date ? date : new Date(date);
  return d
    .toString()
    .split(' ')
    .filter((x, i) => i === 1 || i === 3)
    .join(' ');
};

/**
 * Checks if the date object is valid
 * @param date The date object you wish to check
 * @returns If false it is an invalid object if true it is a valid object
 */
export const isValidDate = (date: Date): boolean => {
  let res = false;

  if (Object.prototype.toString.call(date) === '[object Date]') {
    // it is a date
    res = true;
    if (isNaN(date.getTime())) {
      // date is not valid
      res = false;
    } else {
      // date is valid
      res = true;
    }
  } else {
    // not a date
    res = false;
  }

  return res;
};

/**
 * Returns the month difference between two dates (ignoring the day).
 * A negative value means `from` and `to` dates are reversed
 * (`to` was supplied first).
 */
export const monthDiff = (from: Date, to: Date) => {
  const fromYear = from.getFullYear();
  const toYear = to.getFullYear();
  const fromMonth = from.getMonth();
  const toMonth = to.getMonth();

  const diff = toMonth + 12 * toYear - (fromMonth + 12 * fromYear);
  return diff;
};

/**
 * For a given date returns a new date object that is set to the last
 * day of that month
 */
export const getLastDayOfMonth = (date: Date): Date => {
  return isValidDate(date) ? new Date(date.getFullYear(), date.getMonth() + 1, 0) : null;
};

export const getFirstDayOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

/**
 * For a given date, adds `increment` months and returns its last day.
 * @param increment If not provided, defaults to 1.
 */
export const getLastDayOfNextMonth = (date: Date, increment = 1): Date => {
  const newDate = getFirstDayOfMonth(date);
  newDate.setMonth(newDate.getMonth() + increment);
  return getLastDayOfMonth(newDate);
};

/**
 * filters a time series by mm-yy will return an array of values within the range of the start and end date
 * @returns [startIndex, endIndex ]
 */
export const filterTimeSeriesByMonthYear = (startDate: Date, endDate: Date, dates: string[]): number[] => {
  // const realDates = dates.map(getUTCDate);
  // const realDates = dates.map(d => new Date(d));

  const startIndex = dates.findIndex(d => getDateString(d).substr(0, 7) === getDateString(startDate).substr(0, 7));
  const endIndex = dates.findIndex(d => getDateString(d).substr(0, 7) === getDateString(endDate).substr(0, 7));

  return [startIndex, endIndex];
};

export const isSameMonthYear = (dateToTest: Date, compareTo: Date) => {
  return getLastDayOfMonth(dateToTest).getTime() === getLastDayOfMonth(compareTo).getTime();
};

export const getLastObservationDateFromCalibration = (calibration: any) => {
  let lastObsDate;
  try {
    // ! Fix 6361
    lastObsDate = calibration.hasOwnProperty('ChangeLogs')
      ? // It's a level 1 item
        calibration.ChangeLogs[0].Calibration.LastObservationDate
      : // It's a level 3 item
        calibration.LastObservationDate;
  } catch (err) {
    // ...
  }
  return lastObsDate || null;
};

/**
 * Examples:
 * * `getDuration(234234)`
 * * `getDuration(date1, date2)`
 * * `getDuration(date1)`
 *
 * Result: `2 days, 17 hours, 3 mins, 54 secs`
 *
 * @param start Seconds or Start Date
 * @param end End Date (optional), ignored if _start_ is seconds.
 */
export const getDuration = (start: Date | number, end: Date = null): string => {
  let seconds = 0;
  if (typeof start === 'number') {
    seconds = start;
  } else {
    if (end === null) {
      end = new Date();
    }
    seconds = Math.ceil((end.getTime() - start.getTime()) / 1000);
  }

  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  const dDisplay = d > 0 ? d + (d === 1 ? ' day, ' : ' days, ') : '';
  const hDisplay = h > 0 ? h + (h === 1 ? ' hour, ' : ' hours, ') : '';
  const mDisplay = m > 0 ? m + (m === 1 ? ' min, ' : ' mins, ') : '';
  const sDisplay = s > 0 ? s + (s <= 1 ? ' sec,' : ' secs,') : '';

  return (dDisplay + hDisplay + mDisplay + sDisplay).trim().slice(0, -1);
};

export const minDate = (...dates: Date[]): Date => new Date(Math.min.apply(null, dates));

export const rangeOfDates = (start: Date, end: Date) =>
  Array(monthDiff(start, end) + 1)
    .fill(start)
    .map((endOfMonth, index) => getLastDayOfNextMonth(new Date(endOfMonth), index));
